import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';
import { Alert } from '@televet/kibble-ui/Alert';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { useAstroPetTRPC } from '@/lib/trpcContext';
import { DecisionTreeContent } from '@/features/workflowTask/DecisionTreeContent';
import { useDecisionTree } from '@/features/workflowTask/useDecisionTree';
import { useIntentTaskId } from '@/features/workflowTask/useIntentTaskId';
import type { MenuState } from '@/features/workflowTask/types';

/**
 * The standalone appointment-request page: `/workflow-tasks?taskId=…`, or `?intentId=…` from a
 * workflow message link.
 *
 * ⛔ **The id is a QUERY param, not a path segment, and that is forced.** A workflow link's
 * `targetUrl` is static — `contentLinkShared.ts` rejects a `{{…}}` in it outright, because
 * dispatch never template-resolves the target and the braces would ship percent-encoded. Only
 * `dynamicParams` VALUES are resolved, and those can only become query params. So a path-param
 * route is unreachable from the link config that has to address it.
 *
 * Message links carry `intentId`, not `taskId`: the message is sent by a sibling SendMessage task,
 * so the sender's `workflowRunTaskId` is not the tree. See `useIntentTaskId`.
 *
 * ⛔ **Astro renders this itself.** The same decision tree also ships as a module-federated modal
 * that legacy pet web (care-web) mounts on its home page, and until this route existed that was
 * the ONLY way a pet parent could answer one. Both surfaces share `@/features/workflowTask` —
 * one tree implementation, two shells.
 *
 * ⛔ **No redirect, deliberately.** `submitSelection` can return a terminal `action` of
 * `internal-redirect`, `external-redirect` or `redirect`, and the modal acts on all three because
 * it sits inside a portal that has somewhere to go. This page does not: the parent arrived from a
 * link, answered the question, and is done. `onTerminalAction` therefore always returns
 * `confirm`, which renders the tree's own terminal prompt as the last screen — with no button
 * under it, since there is nowhere to go next.
 *
 * ⚠️ **The pet and clinic names come from the run's OWN Flow database.** Requests are emitted in
 * both the `Owl` and `Yak` namespaces, and a `Yak` run's `ClinicPet` and `Clinic` live only in
 * yak. The tree itself needs no such routing — it is in the astro database, which is single.
 * `getTaskPage` picks the Flow client per run; see
 * `apps/api/src/services/workflowTask/runContext.ts`.
 */
function WorkflowTaskRoute(): React.JSX.Element {
  const search = Route.useSearch();
  const trpc = useAstroPetTRPC();
  const { taskId, resolveError, retryResolve } = useIntentTaskId(search);

  const { data, isPending, isError, error, refetch } = useQuery({
    ...trpc.workflow.getTaskPage.queryOptions({ taskId }),
    // A link with no id addresses nothing, so there is no request to make.
    enabled: taskId !== '',
    // One attempt. Every way this fails — expired link, wrong parent, no such task — is a refusal
    // no amount of retrying changes, and react-query's default three tries with backoff leaves the
    // parent watching a spinner for seconds before the message they need appears. Same call the
    // nutrition invite resolver makes, for the same reason. Retry is still offered on the error.
    retry: false,
  });

  const controller = useDecisionTree({
    taskId,
    serverState: (data?.state as MenuState | undefined) ?? null,
    // This surface never navigates away, so the tree always shows its confirmation screen.
    onTerminalAction: () => 'confirm',
  });

  const clinicPhoneNumber = data?.context.clinicPhoneNumber ?? null;

  /**
   * A task already answered — in an earlier session, or in the pet portal. The server refuses a
   * second submission, so saying so is better than rendering options that cannot be used.
   * Suppressed once this session has completed the tree, which has its own terminal screen.
   */
  const wasAlreadyAnswered =
    data?.state.completed === true && controller.confirmationPrompt === null;

  const callYourClinic =
    clinicPhoneNumber !== null
      ? `Call ${clinicPhoneNumber} if you need to change anything.`
      : 'Call your clinic if you need to change anything.';

  /**
   * UNAUTHORIZED means the link carried no usable token — most often an expired tracked link,
   * which `/t/:slug` deliberately redirects WITHOUT one rather than killing. The raw tRPC message
   * is the bare code, so it gets its own copy; everything else shows what the server said.
   */
  const failure = resolveError ?? (isError ? error : null);
  const loadError =
    failure === null
      ? null
      : failure.data?.code === 'UNAUTHORIZED'
        ? 'This link has expired. Please use the most recent message from your clinic, or call them directly.'
        : (failure.message ?? 'We could not load this request. Please try again.');

  /**
   * A link with no id addresses nothing, so the query is disabled and would otherwise leave
   * the page on a spinner forever. Its own copy rather than the expired-link message: nothing is
   * wrong with the parent's session, the link itself is malformed, and telling them to re-open it
   * would send them round the same loop.
   */
  if (search.taskId === '' && search.intentId === '') {
    return (
      <Flex
        justify="center"
        bg="tertiary.50"
        minH="100dvh"
        py={12}
        px={3}
        data-testid="workflow-task-page"
      >
        <Alert
          data-testid="workflow-task-error"
          status="error"
          title="This link is missing information we need."
          description="Please try the link from your message again, or call your clinic."
          hideCloseButton
        />
      </Flex>
    );
  }

  return (
    // `tertiary.50` rather than `background.subtle`: the Figma design's page tint is #FAF8F2, and
    // there's no semantic `background.*` alias for it (those only cover neutral/primary/secondary
    // scales) — the core palette entry is the only way to hit this exact color. Same call made in
    // FormShell.tsx for the nutrition intake page.
    <Flex
      justify="center"
      bg="tertiary.50"
      minH="100dvh"
      px={3}
      py={8}
      data-testid="workflow-task-page"
    >
      <VStack gap={5} width="100%" maxWidth="480px" alignItems="stretch">
        {data !== undefined && (
          <Heading data-testid="workflow-task-pet-name" size="md" textAlign="center">
            {data.context.petName}
          </Heading>
        )}
        <Box
          bg="background.default"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="lg"
          p={6}
        >
          {wasAlreadyAnswered ? (
            <Alert
              data-testid="workflow-task-already-answered"
              status="success"
              title="You've already answered this."
              description={callYourClinic}
              hideCloseButton
            />
          ) : (
            <DecisionTreeContent
              controller={controller}
              isLoading={isPending && loadError === null}
              loadError={loadError}
              onRetryLoad={resolveError !== null ? retryResolve : () => void refetch()}
            />
          )}
        </Box>

        {((data !== undefined && data.context.clinicName !== null) ||
          (clinicPhoneNumber !== null && !wasAlreadyAnswered)) && (
          <VStack gap={2} alignItems="center">
            {data !== undefined && data.context.clinicName !== null && (
              <Text size="sm" variant="subtle" textAlign="center">
                {data.context.clinicName}
              </Text>
            )}
            {clinicPhoneNumber !== null && !wasAlreadyAnswered && (
              <Text size="sm" variant="subtle" textAlign="center">
                Questions? Call {clinicPhoneNumber}.
              </Text>
            )}
          </VStack>
        )}
      </VStack>
    </Flex>
  );
}

/**
 * Both ids default to empty rather than failing validation: a malformed link should reach the
 * page and show its own copy, not a router error screen the parent cannot act on.
 */
const workflowTaskSearchSchema = z.object({
  taskId: z.string().catch(''),
  intentId: z.string().catch(''),
});

export const Route = createFileRoute('/workflow-tasks')({
  component: WorkflowTaskRoute,
  validateSearch: workflowTaskSearchSchema,
});
